import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import MockNewOrderDialog from './MockNewOrderDialog';
import MockOrderDetailsDialog from './MockOrderDetailsDialog';
import MockRequisitionFormDialog from './MockRequisitionFormDialog';

const drawerItems = [
  { key: 'new-order', label: 'New Order' },
  { key: 'orders', label: 'Orders' },
];

const laboratoryTests = [
  {
    id: 'neuro-inflammatory-panel',
    title: 'Neuro Inflammatory Panel',
    description:
      'Multi-marker blood test used as a placeholder for neuroinflammation and immune response review.',
    image:
      'https://images.pexels.com/photos/4047146/pexels-photo-4047146.jpeg?auto=compress&cs=tinysrgb&w=600',
  },
  {
    id: 'plasma-biomarker-assay',
    title: 'Plasma Biomarker Assay',
    description:
      'Placeholder assay card for plasma-based biomarker screening and longitudinal monitoring.',
    image:
      'https://images.pexels.com/photos/6285359/pexels-photo-6285359.jpeg?auto=compress&cs=tinysrgb&w=600',
  },
  {
    id: 'serology-confirmation',
    title: 'Serology Confirmation',
    description:
      'Confirmatory laboratory workflow placeholder for antibody and clinical serology evaluation.',
    image:
      'https://images.pexels.com/photos/6622520/pexels-photo-6622520.jpeg?auto=compress&cs=tinysrgb&w=600',
  },
  {
    id: 'specimen-integrity-check',
    title: 'Specimen Integrity Check',
    description:
      'Pre-analytic review card for sample condition, labeling quality, and accession readiness.',
    image:
      'https://images.pexels.com/photos/8442446/pexels-photo-8442446.jpeg?auto=compress&cs=tinysrgb&w=600',
  },
  {
    id: 'clinical-chemistry-screen',
    title: 'Clinical Chemistry Screen',
    description:
      'General chemistry placeholder test for screening analytes and monitoring follow-up trends.',
    image:
      'https://images.pexels.com/photos/2280549/pexels-photo-2280549.jpeg?auto=compress&cs=tinysrgb&w=600',
  },
];

const mockOrders = [
  {
    id: 'ORD-24091',
    patient: 'Eleanor Bishop',
    patientEmail: 'eleanor.bishop@example.com',
    patientPhone: '(843) 555-0182',
    test: 'Neuro Inflammatory Panel',
    status: 'Pending Payment',
    delivery: 'Deliver Kit',
    createdAt: '2026-03-28',
  },
  {
    id: 'ORD-24088',
    patient: 'Paul Kim',
    patientEmail: 'paul.kim@example.com',
    patientPhone: '(912) 555-0144',
    test: 'Plasma Biomarker Assay',
    status: 'Kit Preparing',
    delivery: 'Deliver Kit',
    createdAt: '2026-03-27',
  },
  {
    id: 'ORD-24079',
    patient: 'Miriam Cole',
    patientEmail: 'miriam.cole@example.com',
    patientPhone: '(813) 555-0176',
    test: 'Clinical Chemistry Screen',
    status: 'In Transit',
    delivery: 'In House Inventory',
    createdAt: '2026-03-26',
  },
  {
    id: 'ORD-24074',
    patient: 'Noah Diaz',
    patientEmail: 'noah.diaz@example.com',
    patientPhone: '(904) 555-0119',
    test: 'Serology Confirmation',
    status: 'Collected',
    delivery: 'In House Inventory',
    createdAt: '2026-03-25',
  },
  {
    id: 'ORD-24070',
    patient: 'Ava Lewis',
    patientEmail: 'ava.lewis@example.com',
    patientPhone: '(404) 555-0114',
    test: 'Specimen Integrity Check',
    status: 'Results Ready',
    delivery: 'Deliver Kit',
    createdAt: '2026-03-24',
  },
  {
    id: 'ORD-24064',
    patient: 'Liam Moore',
    patientEmail: 'liam.moore@example.com',
    patientPhone: '(615) 555-0191',
    test: 'Neuro Inflammatory Panel',
    status: 'Cancelled',
    delivery: 'Deliver Kit',
    createdAt: '2026-03-22',
  },
];

export default function ProviderOrdersMock() {
  const navigate = useNavigate();
  const location = useLocation();
  const hashKey = location.hash.replace('#', '');
  const activeSection = drawerItems.some((item) => item.key === hashKey) ? hashKey : 'new-order';
  const [searchText, setSearchText] = React.useState('');
  const [selectedTest, setSelectedTest] = React.useState(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedOrder, setSelectedOrder] = React.useState(null);
  const [orderDialogOpen, setOrderDialogOpen] = React.useState(false);
  const [requisitionDialogOpen, setRequisitionDialogOpen] = React.useState(false);
  const [requisitionOrderLabel, setRequisitionOrderLabel] = React.useState('');
  const [orderStatusFilter, setOrderStatusFilter] = React.useState('all');
  const [deliveryFilter, setDeliveryFilter] = React.useState('all');

  React.useEffect(() => {
    if (!hashKey || !drawerItems.some((item) => item.key === hashKey)) {
      navigate(`${location.pathname}#new-order`, { replace: true });
    }
  }, [hashKey, location.pathname, navigate]);

  const filteredTests = laboratoryTests.filter((test) => {
    const query = searchText.trim().toLowerCase();
    if (!query) {
      return true;
    }
    return `${test.title} ${test.description}`.toLowerCase().includes(query);
  });
  const filteredOrders = mockOrders.filter((order) => {
    const query = searchText.trim().toLowerCase();
    const matchesSearch =
      !query ||
      `${order.id} ${order.patient} ${order.test}`.toLowerCase().includes(query);
    const matchesStatus = orderStatusFilter === 'all' || order.status === orderStatusFilter;
    const matchesDelivery = deliveryFilter === 'all' || order.delivery === deliveryFilter;
    return matchesSearch && matchesStatus && matchesDelivery;
  });

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} sx={{ maxWidth: 1120, mx: 'auto' }}>
        <Card
          sx={{
            width: { xs: '100%', md: 260 },
            border: '1px solid #d8e2e7',
            boxShadow: 'none',
            borderRadius: 2,
            alignSelf: 'flex-start',
          }}
        >
          <CardContent sx={{ p: 1 }}>
            <Stack spacing={0.5}>
              {drawerItems.map((item) => (
                <Button
                  key={item.key}
                  onClick={() => navigate(`${location.pathname}#${item.key}`)}
                  variant={activeSection === item.key ? 'contained' : 'text'}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 600 }}
                >
                  {item.label}
                </Button>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Box sx={{ flex: 1 }}>
          <Card sx={{ border: '1px solid #d8e2e7', boxShadow: 'none', borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              {activeSection === 'new-order' ? (
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
                      New Order
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Select a laboratory test from the placeholder catalog below.
                    </Typography>
                  </Box>

                  <TextField
                    label="Filter Laboratory Tests"
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    size="small"
                    fullWidth
                  />

                  <Stack spacing={2}>
                    {filteredTests.map((test) => (
                      <Card
                        key={test.id}
                        sx={{
                          border: '1px solid #d8e2e7',
                          boxShadow: 'none',
                          borderRadius: 2,
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <Box
                              component="img"
                              src={test.image}
                              alt={test.title}
                              sx={{
                                width: { xs: '100%', sm: 180 },
                                height: 120,
                                objectFit: 'cover',
                                borderRadius: 1.5,
                                flexShrink: 0,
                                backgroundColor: '#eef3f7',
                              }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="h6" sx={{ mb: 1 }}>
                                {test.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {test.description}
                              </Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => {
                                    setSelectedTest(test);
                                    setDialogOpen(true);
                                  }}
                                >
                                  Select
                                </Button>
                              </Box>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}

                    {filteredTests.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No laboratory tests match the current filter.
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              ) : (
                <Stack spacing={2.5}>
                  <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
                    Orders
                  </Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                    <TextField
                      label="Search Orders"
                      value={searchText}
                      onChange={(event) => setSearchText(event.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      select
                      label="Status"
                      value={orderStatusFilter}
                      onChange={(event) => setOrderStatusFilter(event.target.value)}
                      size="small"
                      sx={{ minWidth: 200 }}
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      {Array.from(new Set(mockOrders.map((order) => order.status))).map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Delivery"
                      value={deliveryFilter}
                      onChange={(event) => setDeliveryFilter(event.target.value)}
                      size="small"
                      sx={{ minWidth: 200 }}
                    >
                      <MenuItem value="all">All Delivery Types</MenuItem>
                      {Array.from(new Set(mockOrders.map((order) => order.delivery))).map((delivery) => (
                        <MenuItem key={delivery} value={delivery}>
                          {delivery}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>

                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Order ID</TableCell>
                        <TableCell>Patient</TableCell>
                        <TableCell>Test</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Delivery</TableCell>
                        <TableCell>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredOrders.map((order) => (
                        <TableRow
                          key={order.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => {
                            setSelectedOrder(order);
                            setOrderDialogOpen(true);
                          }}
                        >
                          <TableCell>{order.id}</TableCell>
                          <TableCell>{order.patient}</TableCell>
                          <TableCell>{order.test}</TableCell>
                          <TableCell>{order.status}</TableCell>
                          <TableCell>{order.delivery}</TableCell>
                          <TableCell>{order.createdAt}</TableCell>
                        </TableRow>
                      ))}
                      {filteredOrders.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Typography variant="body2" color="text.secondary">
                              No orders match the current filters.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Box>
      </Stack>
      <MockNewOrderDialog
        open={dialogOpen}
        selectedTest={selectedTest}
        onOrderCreated={() => {
          setRequisitionOrderLabel('NEW-MOCK-ORDER');
          setRequisitionDialogOpen(true);
        }}
        onClose={() => {
          setDialogOpen(false);
          setSelectedTest(null);
        }}
      />
      <MockOrderDetailsDialog
        open={orderDialogOpen}
        order={selectedOrder}
        onShowRequisition={(order) => {
          setRequisitionOrderLabel(order?.id || '');
          setRequisitionDialogOpen(true);
        }}
        onClose={() => {
          setOrderDialogOpen(false);
          setSelectedOrder(null);
        }}
      />
      <MockRequisitionFormDialog
        open={requisitionDialogOpen}
        orderLabel={requisitionOrderLabel}
        onClose={() => {
          setRequisitionDialogOpen(false);
          setRequisitionOrderLabel('');
        }}
      />
    </Container>
  );
}
